import { AnimatePresence, motion } from 'framer-motion';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import AppFooter from './components/shared/AppFooter';
import AppHeader from './components/shared/AppHeader';
import './css/main.css';
import './css/App.css';
import UseScrollToTop from './hooks/useScrollToTop';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { ThemeProvider, useTheme } from './context/ThemeContext';

const About = lazy(() => import('./pages/AboutMe'));
const Contact = lazy(() => import('./pages/Contact.jsx'));
const Home = lazy(() => import('./pages/Home'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectSingle = lazy(() => import('./pages/ProjectSingle.jsx'));

// Enhanced loading component
const LoadingSpinner = () => (
	<div className="min-h-screen flex items-center justify-center bg-blue-50 dark:bg-blue-900">
		<div className="relative">
			<div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
			<div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-blue-600 rounded-full animate-spin" style={{ animationDelay: '-0.5s' }}></div>
		</div>
	</div>
);

// Page transition wrapper
const PageTransition = ({ children }) => (
	<motion.div
		initial={{ opacity: 0, y: 20 }}
		animate={{ opacity: 1, y: 0 }}
		exit={{ opacity: 0, y: -20 }}
		transition={{ duration: 0.5, ease: "easeInOut" }}
	>
		{children}
	</motion.div>
);

// Theme initialization component
const ThemeInitializer = () => {
	const { theme, mounted } = useTheme();
	
	useEffect(() => {
		if (mounted) {
			// Ensure theme is applied to document
			const root = document.documentElement;
			root.classList.remove('light', 'dark');
			root.classList.add(theme);
			root.setAttribute('data-theme', theme);
			
			console.log('App: Theme initialized to:', theme);
		}
	}, [theme, mounted]);
	
	return null;
};

function App() {
	useEffect(() => {
		AOS.init({
			duration: 1000,
			easing: 'ease-in-out',
			once: true,
			mirror: false
		});
	}, []);

	return (
		<ThemeProvider>
			<ThemeInitializer />
			<AnimatePresence mode="wait">
				<div className="min-h-screen bg-blue-50 dark:bg-blue-900 transition-all duration-700">
					<Router>
						<ScrollToTop />
						<AppHeader />
						<Suspense fallback={<LoadingSpinner />}>
							<Routes>
								<Route path="/" element={
									<PageTransition>
										<Home />
									</PageTransition>
								} />
								<Route path="projects" element={
									<PageTransition>
										<Projects />
									</PageTransition>
								} />
								<Route
									path="projects/single-project"
									element={
										<PageTransition>
											<ProjectSingle />
										</PageTransition>
									}
								/>
								<Route path="about" element={
									<PageTransition>
										<About />
									</PageTransition>
								} />
								<Route path="contact" element={
									<PageTransition>
										<Contact />
									</PageTransition>
								} />
							</Routes>
						</Suspense>
						<AppFooter />
					</Router>
					<UseScrollToTop />
				</div>
			</AnimatePresence>
		</ThemeProvider>
	);
}

export default App;
